export type Language = "pt" | "es" | "en";

export const translations = {
  // Header
  header: {
    features: { pt: "Recursos", es: "Características", en: "Features" },
    howItWorks: { pt: "Como Funciona", es: "Cómo Funciona", en: "How It Works" },
    dashboard: { pt: "Dashboard", es: "Dashboard", en: "Dashboard" },
    pricing: { pt: "Preços", es: "Precios", en: "Pricing" },
    contact: { pt: "Entrar em Contato", es: "Contáctenos", en: "Contact Us" },
    threatMap: { pt: "Mapa de Ameaças", es: "Mapa de Amenazas", en: "Threat Map" },
  },

  // Hero
  hero: {
    badge: { pt: "Sem Agente · Sem Instalação", es: "Sin Agente · Sin Instalación", en: "Agentless · Zero Installation" },
    titleLine1: { pt: "Proteja sua Empresa", es: "Proteja su Empresa", en: "Protect Your Business" },
    titleLine2: { pt: "antes que seja tarde", es: "antes de que sea tarde", en: "before it's too late" },
    subtitle: {
      pt: "Veja e proteja sua superfície de ataque externa — do jeito que um atacante a vê.",
      es: "Vea y proteja su superficie de ataque externa — tal como la ve un atacante.",
      en: "See and protect your external attack surface — the way an attacker sees it.",
    },
    cta: { pt: "Agendar Demonstração", es: "Agendar Demostración", en: "Schedule a Demo" },
    scrollIndicator: { pt: "Rolar para baixo para explorar", es: "Desplazarse hacia abajo para explorar", en: "Scroll down to explore" },
    stats: {
      checks: { pt: "Verificações", es: "Verificaciones", en: "Checks" },
      monitoring: { pt: "Monitoramento", es: "Monitoreo", en: "Monitoring" },
      impact: { pt: "Impacto", es: "Impacto", en: "Impact" },
      firstScan: { pt: "Primeiro Scan", es: "Primer Escaneo", en: "First Scan" },
    },
  },

  // Features
  features: {
    title: { pt: "Visão completa do seu", es: "Visión completa de su", en: "Complete view of your" },
    titleHighlight: { pt: " perímetro externo", es: " perímetro externo", en: " external perimeter" },
    subtitle: {
      pt: "Descoberta contínua de ativos, análise de vulnerabilidades e insights por IA — tudo sem impactar sua infraestrutura.",
      es: "Descubrimiento continuo de activos, análisis de vulnerabilidades e insights con IA — todo sin impactar su infraestructura.",
      en: "Continuous asset discovery, vulnerability analysis and AI-powered insights — all without impacting your infrastructure.",
    },
    items: [
      {
        title: { pt: "Descoberta da Superfície Externa", es: "Descubrimiento de la Superficie Externa", en: "External Surface Discovery" },
        description: {
          pt: "Mapeia domínios, subdomínios, IPs, portas, serviços e certificados — inclusive a origem real escondida atrás de CDN.",
          es: "Mapea dominios, subdominios, IPs, puertos, servicios y certificados — incluso el origen real oculto detrás del CDN.",
          en: "Maps domains, subdomains, IPs, ports, services and certificates — including the real origin hidden behind CDNs.",
        },
      },
      {
        title: { pt: "IP Shield", es: "IP Shield", en: "IP Shield" },
        description: {
          pt: "Inventário de todos os IPs expostos: organização, portas abertas, detecção de CDN/WAF/IPS e risco por IP.",
          es: "Inventario de todas las IPs expuestas: organización, puertos abiertos, detección de CDN/WAF/IPS y riesgo por IP.",
          en: "Inventory of every exposed IP: organization, open ports, CDN/WAF/IPS detection and per-IP risk.",
        },
      },
      {
        title: { pt: "Web Shield", es: "Web Shield", en: "Web Shield" },
        description: {
          pt: "Nota A–F para cada domínio e subdomínio, avaliando cabeçalhos de segurança, TLS/HTTPS, e-mail/DNS e falhas confirmadas.",
          es: "Nota A–F para cada dominio y subdominio, evaluando cabeceras de seguridad, TLS/HTTPS, correo/DNS y fallas confirmadas.",
          en: "A–F grade for every domain and subdomain, assessing security headers, TLS/HTTPS, email/DNS and confirmed flaws.",
        },
      },
      {
        title: { pt: "Vulnerabilidades Unificadas", es: "Vulnerabilidades Unificadas", en: "Unified Vulnerabilities" },
        description: {
          pt: "Visão única de vulnerabilidades de rede e web com severidade, CVSS, exploração ativa (KEV) e nível de confiança.",
          es: "Visión única de vulnerabilidades de red y web con severidad, CVSS, explotación activa (KEV) y nivel de confianza.",
          en: "Single view of network and web vulnerabilities with severity, CVSS, active exploitation (KEV) and confidence level.",
        },
      },
      {
        title: { pt: "Vetor de Ataque", es: "Vector de Ataque", en: "Attack Vector" },
        description: {
          pt: "Radar por vetor de ataque — rede, adjacente, local e físico — baseado em CVSS, para priorizar por onde você é mais atingível.",
          es: "Radar por vector de ataque — red, adyacente, local y físico — basado en CVSS, para priorizar por dónde es más alcanzable.",
          en: "Attack-vector radar — network, adjacent, local and physical — based on CVSS, to prioritize where you're most reachable.",
        },
      },
      {
        title: { pt: "Expirações", es: "Expiraciones", en: "Expirations" },
        description: {
          pt: "Acompanha o vencimento de domínios e certificados TLS, avisando com antecedência para evitar indisponibilidade.",
          es: "Supervisa el vencimiento de dominios y certificados TLS, avisando con antelación para evitar indisponibilidad.",
          en: "Tracks domain and TLS certificate expirations, warning ahead of time to avoid downtime.",
        },
      },
      {
        title: { pt: "Postura de Segurança", es: "Postura de Seguridad", en: "Security Posture" },
        description: {
          pt: "Índice em radar de 5 eixos que resume a saúde da sua superfície externa num número fácil de acompanhar.",
          es: "Índice en radar de 5 ejes que resume la salud de su superficie externa en un número fácil de seguir.",
          en: "A 5-axis radar index that sums up the health of your external surface in one number you can track over time.",
        },
      },
      {
        title: { pt: "Consultor de Segurança com IA", es: "Consultor de Seguridad con IA", en: "AI Security Advisor" },
        description: {
          pt: "A IA explica sua postura em linguagem clara, aponta focos de atenção e recomenda ações — pronto para a diretoria.",
          es: "La IA explica su postura en lenguaje claro, señala focos de atención y recomienda acciones — listo para la dirección.",
          en: "AI explains your posture in plain language, highlights hotspots and recommends actions — board-ready.",
        },
      },
      {
        title: { pt: "Eventos e Mudanças", es: "Eventos y Cambios", en: "Events & Changes" },
        description: {
          pt: "Linha do tempo de tudo que muda na superfície: novas vulnerabilidades, portas, IPs, subdomínios e notas web.",
          es: "Línea de tiempo de todo lo que cambia en la superficie: nuevas vulnerabilidades, puertos, IPs, subdominios y notas web.",
          en: "Timeline of everything that changes on your surface: new vulnerabilities, ports, IPs, subdomains and web grades.",
        },
      },
      {
        title: { pt: "Alertas por E-mail", es: "Alertas por Correo", en: "Email Alerts" },
        description: {
          pt: "Aviso imediato para itens graves e altos, mais um resumo diário por cliente — ativável por conta.",
          es: "Aviso inmediato para ítems graves y altos, más un resumen diario por cliente — activable por cuenta.",
          en: "Instant alerts for critical and high items, plus a daily digest per client — toggleable per account.",
        },
      },
      {
        title: { pt: "Relatórios em PDF", es: "Informes en PDF", en: "PDF Reports" },
        description: {
          pt: "Relatório executivo da postura, vulnerabilidades e evolução — pronto para compartilhar interna e externamente.",
          es: "Informe ejecutivo de la postura, vulnerabilidades y evolución — listo para compartir interna y externamente.",
          en: "Executive report of posture, vulnerabilities and evolution — ready to share internally and externally.",
        },
      },
      {
        title: { pt: "Integrações: IA (MCP) e Monitoramento", es: "Integraciones: IA (MCP) y Monitoreo", en: "Integrations: AI (MCP) & Monitoring" },
        description: {
          pt: "Conecte sua própria IA aos seus dados (somente leitura, via MCP) e leve métricas de segurança ao Zabbix/PRTG pela API.",
          es: "Conecte su propia IA a sus datos (solo lectura, vía MCP) y lleve métricas de seguridad a Zabbix/PRTG por la API.",
          en: "Connect your own AI to your data (read-only, via MCP) and push security metrics to Zabbix/PRTG through the API.",
        },
      },
      {
        title: { pt: "Modo Parceiro", es: "Modo Socio", en: "Partner Mode" },
        description: {
          pt: "Gerencie vários clientes num painel só, com modo demonstração, apresentação e gerador de proposta comercial.",
          es: "Gestione varios clientes en un solo panel, con modo demostración, presentación y generador de propuesta comercial.",
          en: "Manage multiple clients in one panel, with demo mode, presentation and commercial proposal generator.",
        },
      },
    ],
  },

  // Pilares dos recursos (abas)
  featurePillars: [
    {
      name: { pt: "Descubra", es: "Descubra", en: "Discover" },
      tagline: {
        pt: "Todo o seu perímetro externo, mapeado e sempre atualizado.",
        es: "Todo su perímetro externo, mapeado y siempre actualizado.",
        en: "Your entire external perimeter, mapped and always up to date.",
      },
    },
    {
      name: { pt: "Priorize", es: "Priorice", en: "Prioritize" },
      tagline: {
        pt: "Só o risco que importa, com contexto e recomendação.",
        es: "Solo el riesgo que importa, con contexto y recomendación.",
        en: "Only the risk that matters, with context and recommendations.",
      },
    },
    {
      name: { pt: "Acompanhe", es: "Acompañe", en: "Track" },
      tagline: {
        pt: "Alertas, evolução da postura e relatórios prontos para a diretoria.",
        es: "Alertas, evolución de la postura e informes listos para la dirección.",
        en: "Alerts, posture evolution and board-ready reports.",
      },
    },
    {
      name: { pt: "Integre", es: "Integre", en: "Integrate" },
      tagline: {
        pt: "Conecte o SentinelHub ao que sua operação já usa — e revenda.",
        es: "Conecte SentinelHub a lo que su operación ya usa — y revenda.",
        en: "Connect SentinelHub to what your operation already uses — and resell.",
      },
    },
  ],

  // Tour pelas telas do produto (carrossel)
  productTour: {
    title: { pt: "O produto em ", es: "El producto en ", en: "The product in " },
    titleHighlight: { pt: "ação", es: "acción", en: "action" },
    subtitle: {
      pt: "Telas reais do SentinelHub — navegue pelas visões da plataforma.",
      es: "Pantallas reales de SentinelHub — navegue por las vistas de la plataforma.",
      en: "Real SentinelHub screens — browse through the platform views.",
    },
  },

  // How It Works
  howItWorks: {
    title: { pt: "Como", es: "Cómo", en: "How it" },
    titleHighlight: { pt: "funciona", es: "funciona", en: "works" },
    subtitle: {
      pt: "Em 4 passos simples, tenha visibilidade completa do seu perímetro de segurança.",
      es: "En 4 simples pasos, tenga visibilidad completa de su perímetro de seguridad.",
      en: "In 4 simple steps, get complete visibility of your security perimeter.",
    },
    steps: [
      {
        title: { pt: "Cadastre seu domínio", es: "Registre su dominio", en: "Register your domain" },
        description: {
          pt: "Insira seu domínio ou IP público. O processo leva apenas segundos.",
          es: "Ingrese su dominio o IP público. El proceso toma solo segundos.",
          en: "Enter your domain or public IP. The process takes just seconds.",
        },
      },
      {
        title: { pt: "Varredura contínua", es: "Escaneo continuo", en: "Continuous scanning" },
        description: {
          pt: "Nossos scanners analisam seu perímetro externo sem afetar sua infraestrutura.",
          es: "Nuestros scanners analizan su perímetro externo sin afectar su infraestructura.",
          en: "Our scanners analyze your external perimeter without affecting your infrastructure.",
        },
      },
      {
        title: { pt: "Análise inteligente", es: "Análisis inteligente", en: "Smart analysis" },
        description: {
          pt: "Correlacionamos dados e identificamos vulnerabilidades com base em CVEs.",
          es: "Correlacionamos datos e identificamos vulnerabilidades con base en CVEs.",
          en: "We correlate data and identify vulnerabilities based on CVEs.",
        },
      },
      {
        title: { pt: "Relatório e alertas", es: "Reportes y alertas", en: "Reports & alerts" },
        description: {
          pt: "Receba um score de risco e alertas quando novas vulnerabilidades surgirem.",
          es: "Reciba un score de riesgo y alertas cuando surjan nuevas vulnerabilidades.",
          en: "Receive a risk score and alerts when new vulnerabilities emerge.",
        },
      },
    ],
    asyncBadge: { pt: "Não Intrusivo", es: "No Intrusivo", en: "Non-Intrusive" },
    asyncTitle: { pt: "Zero impacto no seu ambiente", es: "Cero impacto en su entorno", en: "Zero impact on your environment" },
    asyncDescription: {
      pt: "Todas as verificações são feitas de fora, do ponto de vista de um atacante — sem agente, sem instalação e sem tráfego agressivo ou invasivo no seu ambiente.",
      es: "Todas las verificaciones se realizan desde fuera, desde el punto de vista de un atacante — sin agente, sin instalación y sin tráfico agresivo o invasivo en su entorno.",
      en: "Every check runs from the outside, from an attacker's point of view — no agent, no installation and no aggressive or invasive traffic in your environment.",
    },
  },

  // Dashboard Preview
  dashboard: {
    title: { pt: "Visão do", es: "Visión del", en: "Environment" },
    titleHighlight: { pt: "Ambiente", es: "Ambiente", en: "Overview" },
    subtitle: {
      pt: "Inventário, exposição e risco do seu ambiente monitorado com IA Insights.",
      es: "Inventario, exposición y riesgo de su entorno monitoreado con IA Insights.",
      en: "Inventory, exposure and risk of your monitored environment with AI Insights.",
    },
    envLabel: { pt: "AMBIENTE", es: "ENTORNO", en: "ENVIRONMENT" },
    envTitle: { pt: "Visão do Ambiente", es: "Visión del Entorno", en: "Environment Overview" },
    envSubtitle: {
      pt: "Inventário, exposição e risco do seu ambiente monitorado",
      es: "Inventario, exposición y riesgo de su entorno monitoreado",
      en: "Inventory, exposure and risk of your monitored environment",
    },
    stats: {
      assets: { label: { pt: "Ativos", es: "Activos", en: "Assets" }, sublabel: { pt: "Monitorados", es: "Monitoreados", en: "Monitored" } },
      ips: { label: { pt: "IPs", es: "IPs", en: "IPs" }, sublabel: { pt: "Descobertos", es: "Descubiertos", en: "Discovered" } },
      domains: { label: { pt: "Domínios", es: "Dominios", en: "Domains" }, sublabel: { pt: "Cadastrados", es: "Registrados", en: "Registered" } },
      subdomains: { label: { pt: "Subdomínios", es: "Subdominios", en: "Subdomains" }, sublabel: { pt: "Identificados", es: "Identificados", en: "Identified" } },
      sensitivePorts: { label: { pt: "Portas Sensíveis", es: "Puertos Sensibles", en: "Sensitive Ports" }, sublabel: { pt: "Abertas", es: "Abiertas", en: "Open" } },
      certs: { label: { pt: "Certificados", es: "Certificados", en: "Certificates" }, sublabel: { pt: "Expiram em 10 dias", es: "Expiran en 10 días", en: "Expire in 10 days" } },
    },
    iaInsights: { pt: "IA Insights", es: "IA Insights", en: "AI Insights" },
    iaInsightsText: {
      pt: "Ambiente com padrão recorrente de stacks web legadas (diversas instâncias Apache 2.4.x, múltiplas versões de nginx e Tomcat/Jenkins) e serviços de gestão/remote access expostos (RDP, SSH, serviços de e-mail, bancos e SMB), além de pontos com Exploitability/EPSS elevados (HTTP/2, módulos mod_proxy/mod_rewrite, SMB, Exim).",
      es: "Entorno con patrón recurrente de stacks web legados (diversas instancias Apache 2.4.x, múltiples versiones de nginx y Tomcat/Jenkins) y servicios de gestión/acceso remoto expuestos (RDP, SSH, servicios de e-mail, bases de datos y SMB), además de puntos con Exploitability/EPSS elevados (HTTP/2, módulos mod_proxy/mod_rewrite, SMB, Exim).",
      en: "Environment with recurring pattern of legacy web stacks (multiple Apache 2.4.x instances, various nginx and Tomcat/Jenkins versions) and exposed management/remote access services (RDP, SSH, email services, databases and SMB), plus points with high Exploitability/EPSS (HTTP/2, mod_proxy/mod_rewrite modules, SMB, Exim).",
    },
    iaInsightsWarning: {
      pt: "Superfície é heterogênea e pública, exigindo mitigação em camadas: P0 (48h) — bloquear/segmentar todo acesso remoto público, aplicar allowlist/VPN/bastion com MFA...",
      es: "Superficie es heterogénea y pública, requiriendo mitigación en capas: P0 (48h) — bloquear/segmentar todo acceso remoto público, aplicar allowlist/VPN/bastion con MFA...",
      en: "Surface is heterogeneous and public, requiring layered mitigation: P0 (48h) — block/segment all public remote access, apply allowlist/VPN/bastion with MFA...",
    },
    riskFactors: { pt: "Fatores de Risco", es: "Factores de Riesgo", en: "Risk Factors" },
    riskDistribution: { pt: "Distribuição por severidade (CVEs)", es: "Distribución por severidad (CVEs)", en: "Distribution by severity (CVEs)" },
    critical: { pt: "Críticas", es: "Críticas", en: "Critical" },
    high: { pt: "Altas", es: "Altas", en: "High" },
    medium: { pt: "Médias", es: "Medias", en: "Medium" },
    low: { pt: "Baixas", es: "Bajas", en: "Low" },
    total: { pt: "Total", es: "Total", en: "Total" },
    epssMax: { pt: "EPSS máx.", es: "EPSS máx.", en: "Max EPSS" },
    cvssMax: { pt: "CVSS máx.", es: "CVSS máx.", en: "Max CVSS" },
    securityRisk: { pt: "Security Risk", es: "Riesgo de Seguridad", en: "Security Risk" },
    scoreLabel: { pt: "Score agregado (baseado no CVSS máximo)", es: "Score agregado (basado en el CVSS máximo)", en: "Aggregated score (based on max CVSS)" },
    score: { pt: "Score", es: "Score", en: "Score" },
    vulnerabilityCritical: { pt: "Vulnerabilidade: Crítica", es: "Vulnerabilidad: Crítica", en: "Vulnerability: Critical" },
    highRiskIPs: { pt: "IPs/Domínios com Maior Risco", es: "IPs/Dominios con Mayor Riesgo", en: "Highest Risk IPs/Domains" },
    prioritizeFixes: { pt: "Priorize correções nos IPs com maior score", es: "Priorice correcciones en los IPs con mayor score", en: "Prioritize fixes on IPs with highest score" },
    problematicDomains: { pt: "Domínios/Subdomínios com problemas", es: "Dominios/Subdominios con problemas", en: "Problematic Domains/Subdomains" },
    openSensitivePorts: { pt: "Portas Sensíveis Abertas", es: "Puertos Sensibles Abiertos", en: "Open Sensitive Ports" },
    portsExposure: { pt: "Exposição que normalmente exige revisão", es: "Exposición que normalmente requiere revisión", en: "Exposure that typically requires review" },
    expiringCerts: { pt: "Certificados a Vencer", es: "Certificados por Vencer", en: "Expiring Certificates" },
    expiringIn10: { pt: "Expiram em até 10 dias", es: "Expiran en hasta 10 días", en: "Expire within 10 days" },
  },

  // Kanban/Risk Dashboard
  kanban: {
    title: { pt: "Dashboard de", es: "Dashboard de", en: "Risk" },
    titleHighlight: { pt: "Risco", es: "Riesgo", en: "Dashboard" },
    subtitle: {
      pt: "Análise de risco por IP, domínio e CVEs com Score EPSS e CVSS.",
      es: "Análisis de riesgo por IP, dominio y CVEs con Score EPSS y CVSS.",
      en: "Risk analysis by IP, domain and CVEs with EPSS and CVSS Score.",
    },
    riskDashboard: { pt: "Dashboard de Risco", es: "Dashboard de Riesgo", en: "Risk Dashboard" },
    lastScan: { pt: "Último scan", es: "Último escaneo", en: "Last scan" },
    legend: {
      critical: { pt: "Crítico", es: "Crítico", en: "Critical" },
      high: { pt: "Alto", es: "Alto", en: "High" },
      medium: { pt: "Médio", es: "Medio", en: "Medium" },
      low: { pt: "Baixo", es: "Bajo", en: "Low" },
    },
    epssDesc: {
      pt: "EPSS indica a chance de exploração das vulnerabilidades nos próximos 30 dias",
      es: "EPSS indica la probabilidad de explotación de las vulnerabilidades en los próximos 30 días",
      en: "EPSS indicates the probability of vulnerability exploitation in the next 30 days",
    },
    highRisk: { pt: "Risco Alto", es: "Riesgo Alto", en: "High Risk" },
    in30days: { pt: "em 30 dias", es: "en 30 días", en: "in 30 days" },
    maxLabel: { pt: "Máxima", es: "Máxima", en: "Maximum" },
    affected: { pt: "Afetados", es: "Afectados", en: "Affected" },
    affectedF: { pt: "Afetadas", es: "Afectadas", en: "Affected" },
    domains: { pt: "Domínios", es: "Dominios", en: "Domains" },
    ports: { pt: "Portas", es: "Puertos", en: "Ports" },
    tableHeaders: {
      ip: { pt: "IP", es: "IP", en: "IP" },
      org: { pt: "Org/CDN", es: "Org/CDN", en: "Org/CDN" },
      domains: { pt: "Domínios/Subdomínios", es: "Dominios/Subdominios", en: "Domains/Subdomains" },
      certs: { pt: "Certificados", es: "Certificados", en: "Certificates" },
      openPorts: { pt: "Portas Abertas", es: "Puertos Abiertos", en: "Open Ports" },
      cves: { pt: "CVEs", es: "CVEs", en: "CVEs" },
      epssMax: { pt: "EPSS Máximo", es: "EPSS Máximo", en: "Max EPSS" },
    },
    itemsPerPage: { pt: "Itens por página", es: "Elementos por página", en: "Items per page" },
    previous: { pt: "Anterior", es: "Anterior", en: "Previous" },
    page: { pt: "Página", es: "Página", en: "Page" },
    of: { pt: "de", es: "de", en: "of" },
    next: { pt: "Próxima", es: "Siguiente", en: "Next" },
    showing: { pt: "Mostrando", es: "Mostrando", en: "Showing" },
    to: { pt: "a", es: "a", en: "to" },
    ips: { pt: "IPs", es: "IPs", en: "IPs" },
  },

  // Report / AI Analysis
  report: {
    title: { pt: "Análise de", es: "Análisis de", en: "AI" },
    titleHighlight: { pt: "IA", es: "IA", en: "Analysis" },
    subtitle: {
      pt: "Resumo executivo e recomendações geradas por IA para cada IP.",
      es: "Resumen ejecutivo y recomendaciones generadas por IA para cada IP.",
      en: "Executive summary and AI-generated recommendations for each IP.",
    },
    aiAnalysis: { pt: "Análise de IA", es: "Análisis de IA", en: "AI Analysis" },
    analyzedAt: { pt: "Analisado em", es: "Analizado en", en: "Analyzed at" },
    executiveSummary: { pt: "Resumo Executivo", es: "Resumen Ejecutivo", en: "Executive Summary" },
    summaryText: {
      pt: "Ambiente com alto risco (riskScore 9.8): serviços remotos e banco expostos publicamente, servidor web com stack legada (Apache 2.4.34) e múltiplas vulnerabilidades críticas/alta explotabilidade reportadas; recomenda-se restringir acessos sensíveis, priorizar correções por EPSS e padronizar upgrades e hardening.",
      es: "Entorno con alto riesgo (riskScore 9.8): servicios remotos y base de datos expuestos públicamente, servidor web con stack legado (Apache 2.4.34) y múltiples vulnerabilidades críticas/alta explotabilidad reportadas; se recomienda restringir accesos sensibles, priorizar correcciones por EPSS y estandarizar upgrades y hardening.",
      en: "High-risk environment (riskScore 9.8): remote services and database publicly exposed, web server with legacy stack (Apache 2.4.34) and multiple critical vulnerabilities/high exploitability reported; restricting sensitive access, prioritizing fixes by EPSS, and standardizing upgrades and hardening is recommended.",
    },
    recommendations: { pt: "Recomendações", es: "Recomendaciones", en: "Recommendations" },
    recs: [
      {
        priority: { pt: "Imediata 48h", es: "Inmediata 48h", en: "Immediate 48h" },
        title: {
          pt: "Restringir/segmentar imediatamente acesso externo a serviços remotos e bancos",
          es: "Restringir/segmentar inmediatamente acceso externo a servicios remotos y bases de datos",
          en: "Immediately restrict/segment external access to remote services and databases",
        },
        description: {
          pt: "Mover RDP/DB para VPN/bastion, aplicar controles de autenticação forte e monitorar tentativas de acesso.",
          es: "Mover RDP/DB a VPN/bastion, aplicar controles de autenticación fuerte y monitorear intentos de acceso.",
          en: "Move RDP/DB to VPN/bastion, apply strong authentication controls and monitor access attempts.",
        },
      },
      {
        priority: { pt: "CurtoPrazo 30d", es: "CortoPlazo 30d", en: "ShortTerm 30d" },
        title: {
          pt: "Priorizar patches/mitigações para o servidor web e componentes PHP",
          es: "Priorizar patches/mitigaciones para el servidor web y componentes PHP",
          en: "Prioritize patches/mitigations for the web server and PHP components",
        },
        description: {
          pt: "Vulnerabilidades críticas e alta explotabilidade; validar WAF, regras de proxy e testes de penetração após correção.",
          es: "Vulnerabilidades críticas y alta explotabilidad; validar WAF, reglas de proxy y pruebas de penetración después de la corrección.",
          en: "Critical vulnerabilities and high exploitability; validate WAF, proxy rules and penetration testing after fix.",
        },
      },
      {
        priority: { pt: "Padronizacao Ambiente", es: "Estandarización Entorno", en: "Environment Standardization" },
        title: {
          pt: "Planejar atualização da stack (Apache/PHP) e hardening",
          es: "Planificar actualización del stack (Apache/PHP) y hardening",
          en: "Plan stack update (Apache/PHP) and hardening",
        },
        description: {
          pt: "(mod_proxy/mod_rewrite/mod_ssl/HTTP2), eliminar versões antigas, padronizar deploys, testes e gerenciamento de patches.",
          es: "(mod_proxy/mod_rewrite/mod_ssl/HTTP2), eliminar versiones antiguas, estandarizar deploys, pruebas y gestión de patches.",
          en: "(mod_proxy/mod_rewrite/mod_ssl/HTTP2), eliminate old versions, standardize deploys, tests and patch management.",
        },
      },
    ],
  },

  // Pricing
  pricing: {
    badge: { pt: "Programa de Parceiros", es: "Programa de Socios", en: "Partner Program" },
    title: { pt: "Revenda segurança e", es: "Revenda seguridad y", en: "Resell security and" },
    titleHighlight: { pt: " aumente sua receita", es: " aumente sus ingresos", en: " grow your revenue" },
    subtitle: {
      pt: "Torne-se um parceiro e ofereça aos seus clientes uma solução completa de monitoramento de superfície de ataque.",
      es: "Conviértase en socio y ofrezca a sus clientes una solución completa de monitoreo de superficie de ataque.",
      en: "Become a partner and offer your clients a complete attack surface monitoring solution.",
    },
    benefits: {
      margins: {
        title: { pt: "Margens Atrativas", es: "Márgenes Atractivos", en: "Attractive Margins" },
        desc: { pt: "Descontos exclusivos para parceiros com margens competitivas de mercado", es: "Descuentos exclusivos para socios con márgenes competitivos de mercado", en: "Exclusive partner discounts with competitive market margins" },
      },
      support: {
        title: { pt: "Suporte Dedicado", es: "Soporte Dedicado", en: "Dedicated Support" },
        desc: { pt: "Canal direto com nossa equipe técnica e comercial para apoio em vendas", es: "Canal directo con nuestro equipo técnico y comercial para apoyo en ventas", en: "Direct channel with our technical and sales team for sales support" },
      },
      materials: {
        title: { pt: "Material de Vendas", es: "Material de Ventas", en: "Sales Materials" },
        desc: { pt: "Apresentações, demos e materiais prontos para seus clientes", es: "Presentaciones, demos y materiales listos para sus clientes", en: "Presentations, demos and ready-made materials for your clients" },
      },
      certification: {
        title: { pt: "Certificação", es: "Certificación", en: "Certification" },
        desc: { pt: "Treinamento e certificação oficial para sua equipe técnica", es: "Entrenamiento y certificación oficial para su equipo técnico", en: "Official training and certification for your technical team" },
      },
    },
    plansAvailable: { pt: "Planos disponíveis para revenda", es: "Planes disponibles para reventa", en: "Plans available for resale" },
    pro: {
      name: { pt: "Profissional", es: "Profesional", en: "Professional" },
      desc: { pt: "Para empresas que buscam visibilidade e controle", es: "Para empresas que buscan visibilidad y control", en: "For companies seeking visibility and control" },
      features: [
        { pt: "1 scan full por mês", es: "1 escaneo completo por mes", en: "1 full scan per month" },
        { pt: "Scanner web aprofundado", es: "Scanner web profundo", en: "Deep web scanner" },
        { pt: "Detecção de WAF/CDN (30+ provedores)", es: "Detección de WAF/CDN (30+ proveedores)", en: "WAF/CDN detection (30+ providers)" },
        { pt: "Detecção de arquivos sensíveis", es: "Detección de archivos sensibles", en: "Sensitive file detection" },
        { pt: "Scanner WordPress", es: "Scanner WordPress", en: "WordPress scanner" },
        { pt: "EPSS (probabilidade de exploração)", es: "EPSS (probabilidad de explotación)", en: "EPSS (exploitation probability)" },
        { pt: "Insights e relatórios por IA", es: "Insights e informes por IA", en: "AI-powered insights & reports" },
        { pt: "CVEs em português", es: "CVEs en español", en: "Localized CVE reports" },
        { pt: "Alertas por email", es: "Alertas por email", en: "Email alerts" },
        { pt: "Suporte por email", es: "Soporte por email", en: "Email support" },
      ],
    },
    enterprise: {
      name: { pt: "Enterprise", es: "Enterprise", en: "Enterprise" },
      desc: { pt: "Reduza o tempo de exposição a vulnerabilidades críticas", es: "Reduzca el tiempo de exposición a vulnerabilidades críticas", en: "Reduce exposure time to critical vulnerabilities" },
      includesAll: { pt: "Tudo do Profissional, com mais frequência:", es: "Todo del Profesional, con más frecuencia:", en: "Everything in Professional, with more frequency:" },
      features: [
        { pt: "1 scan full por semana", es: "1 escaneo completo por semana", en: "1 full scan per week" },
        { pt: "4x mais rápido na detecção de novas ameaças", es: "4x más rápido en la detección de nuevas amenazas", en: "4x faster detection of new threats" },
        { pt: "Menor janela de exposição a vulnerabilidades", es: "Menor ventana de exposición a vulnerabilidades", en: "Shorter vulnerability exposure window" },
        { pt: "Visibilidade semanal da evolução do risco", es: "Visibilidad semanal de la evolución del riesgo", en: "Weekly risk evolution visibility" },
        { pt: "Ideal para ambientes regulados e compliance", es: "Ideal para entornos regulados y compliance", en: "Ideal for regulated environments and compliance" },
      ],
    },
    partnerCTA: { pt: "Interessado em se tornar um parceiro?", es: "¿Interesado en convertirse en socio?", en: "Interested in becoming a partner?" },
    partnerButton: { pt: "Quero ser Parceiro", es: "Quiero ser Socio", en: "Become a Partner" },
  },

  // Contact
  contact: {
    badge: { pt: "Entre em Contato", es: "Contáctenos", en: "Contact Us" },
    title: { pt: "Vamos conversar sobre", es: "Hablemos sobre", en: "Let's talk about" },
    titleHighlight: { pt: " sua segurança", es: " su seguridad", en: " your security" },
    subtitle: {
      pt: "Preencha o formulário abaixo e nossa equipe entrará em contato para agendar uma demonstração personalizada.",
      es: "Complete el formulario a continuación y nuestro equipo se pondrá en contacto para agendar una demostración personalizada.",
      en: "Fill out the form below and our team will contact you to schedule a personalized demo.",
    },
    name: { pt: "Nome completo", es: "Nombre completo", en: "Full name" },
    namePlaceholder: { pt: "Seu nome", es: "Su nombre", en: "Your name" },
    email: { pt: "Email corporativo", es: "Email corporativo", en: "Corporate email" },
    emailPlaceholder: { pt: "seu@empresa.com", es: "su@empresa.com", en: "you@company.com" },
    company: { pt: "Empresa", es: "Empresa", en: "Company" },
    companyPlaceholder: { pt: "Nome da empresa", es: "Nombre de la empresa", en: "Company name" },
    jobTitle: { pt: "Cargo", es: "Cargo", en: "Job title" },
    jobTitlePlaceholder: { pt: "Seu cargo", es: "Su cargo", en: "Your position" },
    phone: { pt: "Telefone", es: "Teléfono", en: "Phone" },
    message: { pt: "Mensagem", es: "Mensaje", en: "Message" },
    messagePlaceholder: {
      pt: "Conte-nos sobre suas necessidades de segurança...",
      es: "Cuéntenos sobre sus necesidades de seguridad...",
      en: "Tell us about your security needs...",
    },
    submit: { pt: "Enviar Mensagem", es: "Enviar Mensaje", en: "Send Message" },
    sending: { pt: "Enviando...", es: "Enviando...", en: "Sending..." },
    // Validation
    nameRequired: { pt: "Nome é obrigatório", es: "Nombre es obligatorio", en: "Name is required" },
    nameMinLength: { pt: "Nome deve ter pelo menos 2 caracteres", es: "Nombre debe tener al menos 2 caracteres", en: "Name must be at least 2 characters" },
    emailRequired: { pt: "Email é obrigatório", es: "Email es obligatorio", en: "Email is required" },
    emailInvalid: { pt: "Email inválido", es: "Email inválido", en: "Invalid email" },
    companyRequired: { pt: "Empresa é obrigatória", es: "Empresa es obligatoria", en: "Company is required" },
    jobTitleRequired: { pt: "Cargo é obrigatório", es: "Cargo es obligatorio", en: "Job title is required" },
    phoneInvalid: { pt: "Telefone deve ter entre 7 e 15 dígitos", es: "Teléfono debe tener entre 7 y 15 dígitos", en: "Phone must have between 7 and 15 digits" },
    // Toast messages
    invalidFields: { pt: "Campos inválidos", es: "Campos inválidos", en: "Invalid fields" },
    fixErrors: { pt: "Por favor, corrija os erros no formulário.", es: "Por favor, corrija los errores en el formulario.", en: "Please fix the errors in the form." },
    verificationNeeded: { pt: "Verificação necessária", es: "Verificación necesaria", en: "Verification required" },
    completeVerification: { pt: "Por favor, complete a verificação de segurança.", es: "Por favor, complete la verificación de seguridad.", en: "Please complete the security verification." },
    sendError: { pt: "Erro ao enviar", es: "Error al enviar", en: "Error sending" },
    tryAgainLater: { pt: "Tente novamente mais tarde.", es: "Inténtelo más tarde.", en: "Please try again later." },
    messageSent: { pt: "Mensagem enviada!", es: "¡Mensaje enviado!", en: "Message sent!" },
    willContactSoon: { pt: "Entraremos em contato em breve.", es: "Nos pondremos en contacto pronto.", en: "We'll be in touch soon." },
  },

  // CTA
  cta: {
    titleLine1: { pt: "Comece a proteger seu", es: "Comience a proteger su", en: "Start protecting your" },
    titleLine2: { pt: "perímetro hoje", es: "perímetro hoy", en: "perimeter today" },
    subtitle: {
      pt: "Agende uma demonstração e veja como o SentinelHub pode proteger seu ambiente. Sem compromisso.",
      es: "Agende una demostración y vea cómo SentinelHub puede proteger su entorno. Sin compromiso.",
      en: "Schedule a demo and see how SentinelHub can protect your environment. No commitment.",
    },
    button: { pt: "Agendar Demonstração", es: "Agendar Demostración", en: "Schedule a Demo" },
    check1: { pt: "Demonstração personalizada", es: "Demostración personalizada", en: "Personalized demo" },
    check2: { pt: "Sem impacto no ambiente", es: "Sin impacto en el entorno", en: "No environment impact" },
    check3: { pt: "Tire suas dúvidas", es: "Resuelva sus dudas", en: "Get your questions answered" },
  },

  // Insights
  insights: {
    badge: { pt: "Insights", es: "Insights", en: "Insights" },
    title: { pt: "Guias de aprimoramentos de ", es: "Guías de mejoras de ", en: "Security Enhancement " },
    titleHighlight: { pt: "segurança", es: "seguridad", en: "Guides" },
    subtitle: {
      pt: "Guias práticos e aprofundados sobre segurança, hardening e boas práticas para proteger sua infraestrutura.",
      es: "Guías prácticas y profundas sobre seguridad, hardening y buenas prácticas para proteger su infraestructura.",
      en: "Practical, in-depth guides on security, hardening and best practices to protect your infrastructure.",
    },
    readMore: { pt: "Ler artigo", es: "Leer artículo", en: "Read article" },
    readTitle: { pt: "Ler", es: "Leer", en: "Read" },
    back: { pt: "Voltar", es: "Volver", en: "Back" },
    toc: { pt: "Índice", es: "Índice", en: "Table of Contents" },
    closeToc: { pt: "Fechar índice", es: "Cerrar índice", en: "Close table of contents" },
    notFound: { pt: "Artigo não encontrado", es: "Artículo no encontrado", en: "Article not found" },
    ctaText: { pt: "Quer descobrir se seu servidor está vulnerável?", es: "¿Quiere descubrir si su servidor es vulnerable?", en: "Want to find out if your server is vulnerable?" },
    ctaButton: { pt: "Agendar uma demonstração do SentinelHub", es: "Agendar una demostración de SentinelHub", en: "Schedule a SentinelHub demo" },
    copyCode: { pt: "Copiar código", es: "Copiar código", en: "Copy code" },
    lightMode: { pt: "Modo claro", es: "Modo claro", en: "Light mode" },
    darkMode: { pt: "Modo escuro", es: "Modo oscuro", en: "Dark mode" },
  },

  // Footer
  footer: {
    description: {
      pt: "Plataforma de segurança perimetral que oferece visibilidade completa do seu ambiente externo sem impactar sua infraestrutura.",
      es: "Plataforma de seguridad perimetral que ofrece visibilidad completa de su entorno externo sin impactar su infraestructura.",
      en: "Perimeter security platform that provides complete visibility of your external environment without impacting your infrastructure.",
    },
    product: { pt: "Produto", es: "Producto", en: "Product" },
    features: { pt: "Recursos", es: "Características", en: "Features" },
    pricing: { pt: "Preços", es: "Precios", en: "Pricing" },
    docs: { pt: "Documentação", es: "Documentación", en: "Documentation" },
    api: { pt: "API", es: "API", en: "API" },
    rights: { pt: "Todos os direitos reservados.", es: "Todos los derechos reservados.", en: "All rights reserved." },
  },

  // Threat Map
  threatMap: {
    badge: { pt: "AO VIVO", es: "EN VIVO", en: "LIVE" },
    title: { pt: "Mapa global de", es: "Mapa global de", en: "Live global" },
    titleHighlight: { pt: " ameaças em tempo real", es: " amenazas en tiempo real", en: " threat map" },
    subtitle: {
      pt: "Dados agregados de fontes públicas (SANS ISC · CISA KEV). Atualizado de hora em hora.",
      es: "Datos agregados de fuentes públicas (SANS ISC · CISA KEV). Actualizado cada hora.",
      en: "Aggregated data from open sources (SANS ISC · CISA KEV). Updated hourly.",
    },
    demoNotice: {
      pt: "Dados reais de fontes abertas. Os arcos animados são representação visual — cada arco é um atacante real amostrado indo para um hub de destino plausível.",
      es: "Datos reales de fuentes abiertas. Los arcos animados son representación visual — cada arco es un atacante real muestreado hacia un hub de destino plausible.",
      en: "Real data from open sources. Animated arcs are a visual representation — each arc is a sampled real attacker heading to a plausible destination hub.",
    },
    counterLabel: {
      pt: "Eventos de ataque hoje (estimado)",
      es: "Eventos de ataque hoy (estimado)",
      en: "Attack events today (estimated)",
    },
    overview: { pt: "Panorama", es: "Panorama", en: "Overview" },
    events: { pt: "Eventos", es: "Eventos", en: "Events" },
    sources: { pt: "IPs de origem", es: "IPs de origen", en: "Source IPs" },
    targets: { pt: "Alvos", es: "Objetivos", en: "Targets" },
    kev: { pt: "CVEs no KEV", es: "CVEs en KEV", en: "KEV CVEs" },
    origins: { pt: "Origens dos ataques", es: "Orígenes de los ataques", en: "Attack origins" },
    services: { pt: "Serviços mais atacados", es: "Servicios más atacados", en: "Most attacked services" },
    topAttackers: { pt: "Top IPs atacantes", es: "Top IPs atacantes", en: "Top attacker IPs" },
    legend: { pt: "Legenda", es: "Leyenda", en: "Legend" },
    liveFeed: { pt: "Ataques ao vivo", es: "Ataques en vivo", en: "Live attacks" },
    kevFeed: { pt: "CISA KEV — vulnerabilidades exploradas", es: "CISA KEV — vulnerabilidades explotadas", en: "CISA KEV — exploited vulnerabilities" },
    lastUpdate: { pt: "Última atualização", es: "Última actualización", en: "Last update" },
    reports: { pt: "reports", es: "reportes", en: "reports" },
    ransomware: { pt: "Usado em ransomware", es: "Usado en ransomware", en: "Used in ransomware" },
    loading: { pt: "Carregando dados…", es: "Cargando datos…", en: "Loading data…" },
    noData: {
      pt: "Nenhum retrato disponível ainda. O primeiro é coletado automaticamente na próxima execução.",
      es: "Aún no hay retrato disponible. El primero se recopila automáticamente en la próxima ejecución.",
      en: "No snapshot available yet. The first is collected automatically on the next run.",
    },
    sourcesTitle: { pt: "Fontes de dados", es: "Fuentes de datos", en: "Data sources" },
    catSSH: { pt: "SSH · Força bruta", es: "SSH · Fuerza bruta", en: "SSH · Brute force" },
    catTelnet: { pt: "Telnet · IoT", es: "Telnet · IoT", en: "Telnet · IoT" },
    catWeb: { pt: "Exploit Web", es: "Exploit Web", en: "Web exploit" },
    catRDP: { pt: "RDP", es: "RDP", en: "RDP" },
    catSMB: { pt: "SMB", es: "SMB", en: "SMB" },
    catTLS: { pt: "TLS · DoT", es: "TLS · DoT", en: "TLS · DoT" },
    catIoT: { pt: "IoT · CoAP", es: "IoT · CoAP", en: "IoT · CoAP" },
    catScan: { pt: "Scan · Recon", es: "Escaneo · Recon", en: "Scan · Recon" },
  },
} as const;

export type TranslationKey = keyof typeof translations;

export function t(obj: Record<Language, string>, lang: Language): string {
  return obj[lang] || obj.pt;
}
